import {defineStore} from 'pinia';
import {ref} from 'vue';

/**
 * Drives the global top-of-page loading bar (see NavigationProgressBar.vue,
 * mounted once in App.vue) shown during route navigation. Gives instant
 * visual feedback on nav clicks even when the destination is a lazy-loaded
 * route chunk or slow to fetch its initial data — wired into router.ts's
 * beforeEach/afterEach/onError.
 */
export const useNavigationProgressStore = defineStore('navigationProgress', () => {
   const progress = ref(0);
   const isVisible = ref(false);

   let hideTimeout: ReturnType<typeof setTimeout> | undefined;
   let trickleTimeout: ReturnType<typeof setTimeout> | undefined;

   const trickle = () => {
      if (!isVisible.value) {
         return;
      }
      // Eases toward 90% without ever completing on its own — finish() takes
      // it the rest of the way once the navigation actually resolves, so the
      // bar never looks "done" while still waiting on a slow chunk/request.
      if (progress.value < 90) {
         progress.value += (90 - progress.value) * 0.1;
      }
      trickleTimeout = setTimeout(trickle, 200);
   };

   /** Call from router.beforeEach — shows the bar and starts easing it forward. */
   const start = () => {
      clearTimeout(hideTimeout);
      clearTimeout(trickleTimeout);
      isVisible.value = true;
      progress.value = 8; // small immediate jump so the click feels registered right away
      trickleTimeout = setTimeout(trickle, 200);
   };

   /** Call from router.afterEach/onError — completes and fades the bar out. */
   const finish = () => {
      clearTimeout(trickleTimeout);
      progress.value = 100;
      hideTimeout = setTimeout(() => {
         isVisible.value = false;
         progress.value = 0;
      }, 250);
   };

   return {progress, isVisible, start, finish};
});
